import React, { memo, useState, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { IAutocompleteOption } from 'transfix-ui/components/Autocomplete'
import {
  getAutosuggestOptions,
  IAutosuggestWrapperProps,
  Autosuggest,
} from 'transfix-ui/components/Autosuggest'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { IDriver } from 'apps/fleet-planner/types'
import { selectActiveDrivers } from 'apps/fleet-planner/utils/selectors'
import { getEntityById } from 'utils/getEntityById'

interface IGetFullNameProps {
  firstName?: string | null
  lastName?: string | null
  email?: string | null
  phone?: string | null
}

export const getFullName = ({
  email = '',
  firstName = '',
  lastName = '',
  phone = '',
}: IGetFullNameProps): string => {
  const hasNoName = !firstName && !lastName
  let fullName = `${firstName} ${lastName}`.trim()

  if (hasNoName && email) {
    fullName = `Unknown driver (${email})`
  } else if (hasNoName && phone) {
    fullName = `Unknown driver (${phone})`
  }

  return fullName
}

export interface INewDriverAutosuggestProps extends IAutosuggestWrapperProps<IDriver> {}

export const NewDriverAutosuggest = memo<INewDriverAutosuggestProps>(
  ({
    dataTest = 'driver-autosuggest',
    disabled: disabledProp,
    error: errorProp,
    highlighted = false,
    onAddEntity: onAddEntityProp,
    onBlur: onBlurProp,
    onCancel: onCancelProp,
    onChange: onChangeProp,
    onFocus: onFocusProp,
    required: requiredProp = false,
    value: valueProp,
  }) => {
    const drivers = useSelector(selectActiveDrivers)
    const { isMobile } = useBreakpointContext()
    const [inputValue, setInputValue] = useState<string>('')

    const options = getAutosuggestOptions({
      options: drivers,
      groupIndex: 1,
      groupName: 'Drivers',
      inputValue,
      mapOption: ({ email, firstName, id = '', lastName, phone }) => ({
        id,
        text: getFullName({ firstName, lastName, email, phone }),
      }),
    })

    const changeHandler = useCallback(
      (option: IAutocompleteOption | undefined) => {
        const driver = getEntityById(drivers, option?.id)
        onChangeProp(driver)
      },
      [drivers, onChangeProp]
    )

    return (
      <Autosuggest
        highlighted={highlighted}
        id='driver-autosuggest'
        label='Driver'
        placeholder='Select a Driver'
        onAddEntity={onAddEntityProp}
        value={valueProp}
        options={options}
        onCancel={onCancelProp}
        onChange={changeHandler}
        onInputChange={setInputValue}
        renderOptionType='withAvatar'
        clearable
        dataTest={dataTest}
        error={errorProp}
        onBlur={onBlurProp}
        disabled={disabledProp}
        selectOnFocus={!isMobile}
        onFocus={onFocusProp}
        required={requiredProp}
        renderGroupType='withoutGroupName'
        startAdornmentType='withAvatar'
      />
    )
  }
)
